import Vue from 'vue';
import VueRouter from 'vue-router';
import Login from '../views/Login.vue';
import Layout from '../layout/index.vue';
import Home from '../views/Home.vue';
import Role from '../views/Role.vue';
import User from '../views/User.vue';
import App from '../views/App.vue';
import Sso from '../views/Sso.vue';

Vue.use(VueRouter);

/**
 * 超级管理员角色名
 * 需与后端保持一致：
 *   - routes/role.js  的 SUPER_ROLE（admin 角色不可删、不可改名）
 *   - utils/perm.js   应用权限的 admin 直通判定
 *   - 用户管理里 role 下拉的取值（来自角色管理模块 Role.roleName）
 */
const ADMIN_ROLE = 'superadmin';

/**
 * 取当前用户角色名
 *
 * 兼容三种存法（见 Login.vue 的改动，推荐第 1 种）：
 *   1. localStorage.sso_role           纯字符串，最简单
 *   2. localStorage.sso_user 为 JSON    { username, role, ... }
 *   3. localStorage.sso_user 为纯文本   只有用户名，取不到角色 -> 非管理员
 */
function getUserRole() {
  // 1. 单独的角色字段
  const plain = localStorage.getItem('sso_role');
  if (plain) return plain;

  // 2. 用户信息对象
  const raw = localStorage.getItem('sso_user');
  if (!raw) return '';
  try {
    const u = JSON.parse(raw);
    if (u && typeof u === 'object') {
      return u.role || u.roleName || (u.user && u.user.role) || '';
    }
  } catch (e) {
    // 纯文本（用户名），没有角色信息
    return '';
  }
  return '';
}

/** 是否超级管理员 */
export function isAdmin() {
  return getUserRole() === ADMIN_ROLE;
}

/** 菜单可见性：没标 adminOnly 的都能看，标了的仅管理员可见 */
export function canSee(meta) {
  if (!meta || !meta.adminOnly) return true;
  return isAdmin();
}

const routes = [
  { path: '/login', name: 'Login', component: Login, meta: { title: '登录' } },
  { path: '/sso', name: 'Sso', component: Sso, meta: { title: 'SSO授权' } },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Home',
        component: Home,
        meta: { title: '首页', icon: 'el-icon-s-home' }
      },
      {
        path: 'role',
        name: 'Role',
        component: Role,
        meta: { title: '角色管理', icon: 'el-icon-user-solid', adminOnly: true }
      },
      {
        path: 'user',
        name: 'User',
        component: User,
        meta: { title: '用户管理', icon: 'el-icon-s-custom', adminOnly: true }
      },
      {
        path: 'app',
        name: 'App',
        component: App,
        meta: { title: '应用管理', icon: 'el-icon-s-management', adminOnly: true }
      }
    ]
  }
];

const router = new VueRouter({ routes });

function safeReturnUrl(url) {
  if (typeof url !== 'string') return '';
  if (!url.startsWith('/')) return '';
  if (url.startsWith('//') || url.includes('://')) return '';
  return url;
}

// 路由守卫：仅检查 token 是否存在，token 有效性由后端验证
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('sso_token');
  // 检查目标路由及其所有父级路由是否要求登录
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  if (requiresAuth && !token) {
    next({ path: '/login', query: { returnUrl: to.fullPath } });
    return;
  }

  // 已登录访问登录页：跳回原目标
  if (to.path === '/login' && token) {
    next(safeReturnUrl(to.query.returnUrl) || '/dashboard');
    return;
  }

  // 管理员专区：非管理员直接拦回首页，防止手输 URL 进入
  // 用 matched.some 而非只看 to.meta，以后把 adminOnly 提到父级也生效
  const adminOnly = to.matched.some(record => record.meta.adminOnly);
  if (adminOnly && !isAdmin()) {
    // 目标就是首页时不再跳转，避免死循环
    if (to.path === '/dashboard') {
      next();
      return;
    }
    next({ path: '/dashboard' });
    return;
  }

  next();
});

export default router;
